import React from 'react';
import { abgGraphqlClient, ProxyGraphClients } from '@atlassian/temp-client-utils';
import { generatedTypesNew } from '@css-xp/graphtypes';
import { DEFAULT_ROWS_PER_PAGE } from '@atlassian/cssxp-utils-entitlement';
import { usePersistedCacheQuery, useGetRelatedDomainsForCurrentTxA } from '@css-xp/ppc-custom-hooks';
import { fetchInternalBillingProfilesForTransactionAccountInPPC } from '../queries/fetch-internal-billing-profiles-for-transaction-account';
import { isEmpty } from 'lodash';

interface UseFetchInternalBillingProfilesForTransactionAccountQueryVariables {
  transactionAccountId: string;
  sortOptions?: generatedTypesNew.SortType;
  fetchNodes?: boolean;
  filterOptions?: generatedTypesNew.Maybe<generatedTypesNew.FilterOption>[] | null;
  skipOption?: boolean;
  numberOfBillingProfilesToFetch?: number;
}

const defaultSearchOptions = {
  first: DEFAULT_ROWS_PER_PAGE,
  offset: 0,
};

export const useFetchInternalBillingProfilesForTransactionAccountQuery = ({
  transactionAccountId,
  sortOptions,
  fetchNodes = true,
  filterOptions,
  skipOption = false,
  numberOfBillingProfilesToFetch = DEFAULT_ROWS_PER_PAGE,
}: UseFetchInternalBillingProfilesForTransactionAccountQueryVariables) => {
  const ansClient = React.useMemo(() => abgGraphqlClient(ProxyGraphClients.ANS), []);
  const { relatedDomains, isLoading: isRelatedDomainsLoading } = useGetRelatedDomainsForCurrentTxA();

  const {
    refetch: refetchInternalBillingProfiles,
    data: internalBillingProfilesData,
    error: internalBillingProfilesError,
    loading: internalBillingProfilesLoading,
  } = usePersistedCacheQuery<
    generatedTypesNew.FetchInternalBillingProfilesForTransactionAccountInPpcQuery,
    generatedTypesNew.FetchInternalBillingProfilesForTransactionAccountInPpcQueryVariables
  >(fetchInternalBillingProfilesForTransactionAccountInPPC, {
    variables: {
      id: transactionAccountId,
      searchOptions: {
        ...defaultSearchOptions,
        first: numberOfBillingProfilesToFetch,
        ...(!isEmpty(sortOptions) && { sort: sortOptions }),
      },
      refetchDefaultPaymentMethod: false,
      filterOptions: filterOptions ?? [],
      fetchNodes: fetchNodes,
      relatedDomains: relatedDomains,
    },
    displayName: 'fetchInternalBillingProfilesForTransactionAccountInPPC',
    skip: !transactionAccountId || skipOption || isRelatedDomainsLoading,
    client: ansClient,
  });

  return {
    refetchInternalBillingProfiles,
    internalBillingProfilesData,
    internalBillingProfilesError,
    internalBillingProfilesLoading: internalBillingProfilesLoading || isRelatedDomainsLoading,
  };
};
